import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { readFragment } from "gql.tada";
import { useStore } from "@/app/store";
import { EDIT_CALCULATION_DETAILS } from "../graphql/mutations";
import { CALCULATION_DETAIL_FRAGMENT } from "../graphql/queries";
import useStaleVersionHandler from "./useStaleVersionHandler";

interface EditDetailsArgs {
  name: string;
  kamName: string;
}

export type EditDetailsResult = "success" | "stale" | "error";

const useEditCalculationDetails = (onConflict: () => void) => {
  const handleStale = useStaleVersionHandler(onConflict);
  const setCalculation = useStore((s) => s.setCalculation);
  const [runMutation, { loading }] = useMutation(EDIT_CALCULATION_DETAILS);

  const editDetails = useCallback(
    async ({ name, kamName }: EditDetailsArgs): Promise<EditDetailsResult> => {
      const { calculation } = useStore.getState();
      if (!calculation) return "error";
      const calledForId = calculation.id;
      const result = await runMutation({
        variables: {
          input: {
            id: calledForId,
            expectedVersion: calculation.version,
            name,
            kamName,
          },
        },
      });
      if (result.error) {
        if (handleStale(result.error)) return "stale";
        console.error("editCalculationDetails failed:", result.error);
        return "error";
      }
      if (result.data?.editCalculationDetails) {
        setCalculation(
          readFragment(
            CALCULATION_DETAIL_FRAGMENT,
            result.data.editCalculationDetails,
          ),
          calledForId,
        );
      }
      return "success";
    },
    [runMutation, handleStale, setCalculation],
  );

  return { editDetails, isSaving: loading };
};

export default useEditCalculationDetails;
